import React, { useState, useRef } from 'react';
import { Upload, Loader2, Check, X, AlertTriangle, Play } from 'lucide-react';
import { AppMode, RoomType, ROOM_LABELS } from '../types';
import { generateVariant, saveRender } from '../services/pipelineService';
import { trackEvent } from '../services/analytics';

const ALL_ROOMS: RoomType[] = [RoomType.LIVING_ROOM, RoomType.BEDROOM, RoomType.KITCHEN, RoomType.BATHROOM];

interface RoomJob {
  roomType: RoomType;
  file: File | null;
  previewUrl: string | null;
  status: 'idle' | 'queued' | 'generating' | 'saving' | 'done' | 'error';
  progress: number;
  error: string | null;
}

interface Props {
  propertyId: number;
  mode: AppMode;
  uncoveredRooms: Set<string>;
  onComplete: () => void;
  onCreditsUpdate: (credits: number) => void;
}

export const BulkGeneratePanel: React.FC<Props> = ({
  propertyId, mode, uncoveredRooms, onComplete, onCreditsUpdate,
}) => {
  const [rooms, setRooms] = useState<RoomJob[]>(
    ALL_ROOMS.map(rt => ({
      roomType: rt,
      file: null,
      previewUrl: null,
      status: 'idle',
      progress: 0,
      error: null,
    }))
  );
  const [selectedRooms, setSelectedRooms] = useState<Set<RoomType>>(
    new Set(ALL_ROOMS.filter(rt => uncoveredRooms.has(rt)))
  );
  const [isRunning, setIsRunning] = useState(false);
  const abortRef = useRef(false);

  const toggleRoom = (rt: RoomType) => {
    if (isRunning) return;
    setSelectedRooms(prev => {
      const next = new Set(prev);
      if (next.has(rt)) next.delete(rt); else next.add(rt);
      return next;
    });
  };

  const handleFileSelect = (rt: RoomType, file: File) => {
    const url = URL.createObjectURL(file);
    setRooms(prev => prev.map(r =>
      r.roomType === rt ? { ...r, file, previewUrl: url } : r
    ));
  };

  const selectedJobs = rooms.filter(r => selectedRooms.has(r.roomType));
  const readyToStart = selectedJobs.length > 0 && selectedJobs.every(r => r.file !== null);
  const doneCount = rooms.filter(r => r.status === 'done').length;
  const errorCount = rooms.filter(r => r.status === 'error').length;

  const startBulk = async () => {
    if (!readyToStart) return;
    setIsRunning(true);
    abortRef.current = false;
    trackEvent('bulk_started', { roomCount: selectedJobs.length });

    const jobQueue = selectedJobs.map(r => r.roomType);

    setRooms(prev => prev.map(r =>
      selectedRooms.has(r.roomType) ? { ...r, status: 'queued', progress: 0, error: null } : r
    ));

    for (const roomType of jobQueue) {
      if (abortRef.current) break;

      setRooms(prev => prev.map(r =>
        r.roomType === roomType ? { ...r, status: 'generating', progress: 10 } : r
      ));

      const job = rooms.find(r => r.roomType === roomType);
      if (!job?.file) continue;

      try {
        const result = await generateVariant({
          file: job.file,
          existingRenovated: null,
          mode,
          roomType,
          // STA-185: pipeline persists the render row with property_id at
          // completion — the saveRender() below dedupes on jobId.
          propertyId,
          onProgress: (_step, progress) => {
            setRooms(prev => prev.map(r =>
              r.roomType === roomType ? { ...r, progress: Math.max(r.progress, progress) } : r
            ));
          },
        });

        setRooms(prev => prev.map(r =>
          r.roomType === roomType ? { ...r, status: 'saving', progress: 90 } : r
        ));

        const saved = await saveRender({
          propertyId,
          stagedImage: result.staged,
          originalImage: result.original,
          roomType,
          mode,
          generationType: 'initial',
          feedbackText: null,
          jobId: result.jobId,
        });

        onCreditsUpdate(saved.credits);

        setRooms(prev => prev.map(r =>
          r.roomType === roomType ? { ...r, status: 'done', progress: 100 } : r
        ));
      } catch (err: any) {
        setRooms(prev => prev.map(r =>
          r.roomType === roomType ? { ...r, status: 'error', error: err.message } : r
        ));
      }
    }

    setIsRunning(false);
    const doneRooms = rooms.filter(r => r.status === 'done').length;
    trackEvent('bulk_completed', { roomCount: jobQueue.length, successCount: doneRooms });
    onComplete();
  };

  const handleAbort = () => {
    abortRef.current = true;
  };

  return (
    <div className="border border-border bg-white p-5 shadow-card mb-6 rounded-lg">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] text-ink/50 uppercase tracking-wide mb-1">
            Bulk generatie
          </p>
          <h3 className="font-sans text-lg font-semibold">
            Meerdere ruimtes tegelijk
          </h3>
        </div>
        {isRunning && (
          <button
            onClick={handleAbort}
            className="text-[10px] font-bold uppercase tracking-wide text-red-600 border border-red-400 px-3 py-1.5 hover:bg-red-50 rounded-md"
          >
            Stoppen
          </button>
        )}
      </div>

      <div className="space-y-3">
        {rooms.map(room => {
          const isSelected = selectedRooms.has(room.roomType);
          const isActive = isRunning && (room.status !== 'idle' && room.status !== 'queued');

          return (
            <div
              key={room.roomType}
              className={`border p-3 transition-colors rounded-md ${
                room.status === 'done' ? 'border-accent bg-accent/10' :
                room.status === 'error' ? 'border-red-400 bg-red-50' :
                isSelected ? 'border-border' : 'border-border/20'
              }`}
            >
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleRoom(room.roomType)}
                  disabled={isRunning}
                  className="w-4 h-4 accent-black"
                />
                <span className="text-sm font-bold flex-1">
                  {ROOM_LABELS[room.roomType]}
                </span>

                {room.status === 'done' && <Check size={16} className="text-ink" />}
                {room.status === 'error' && <AlertTriangle size={16} className="text-red-600" />}
                {(room.status === 'generating' || room.status === 'saving') && (
                  <Loader2 size={16} className="animate-spin text-ink/60" />
                )}

                {isSelected && !isRunning && (
                  <label className="flex items-center gap-2 cursor-pointer border border-dashed border-border/30 hover:border-border px-2 py-1 transition-colors rounded-md">
                    <Upload size={12} className="text-ink/50" />
                    <span className="text-[10px] font-bold uppercase tracking-wide text-ink/60">
                      {room.file ? room.file.name.slice(0, 20) : 'Kies foto'}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={e => {
                        if (e.target.files?.[0]) handleFileSelect(room.roomType, e.target.files[0]);
                      }}
                    />
                  </label>
                )}
              </div>

              {isActive && (
                <div className="mt-2">
                  <div className="w-full bg-stone-100 h-1.5 overflow-hidden rounded-full">
                    <div
                      className="h-full bg-black transition-all duration-500"
                      style={{ width: `${room.progress}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-ink/50 uppercase tracking-wide mt-1">
                    {room.status === 'generating' ? `Genereren… ${room.progress}%` :
                     room.status === 'saving' ? 'Opslaan…' : ''}
                  </p>
                </div>
              )}

              {room.status === 'error' && room.error && (
                <p className="text-[10px] text-red-600 mt-1">{room.error}</p>
              )}

              {room.previewUrl && !isRunning && room.status === 'idle' && (
                <div className="mt-2 h-16 w-24 border border-border/10 overflow-hidden rounded-md">
                  <img src={room.previewUrl} alt={`Voorbeeldfoto van ${ROOM_LABELS[room.roomType] || room.roomType}`} className="w-full h-full object-cover" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-3">
        {!isRunning ? (
          <button
            onClick={startBulk}
            disabled={!readyToStart}
            className={`flex items-center gap-2 px-5 py-3 font-bold text-xs uppercase tracking-wide border border-border transition-all rounded-md ${
              readyToStart
                ? 'bg-ink text-white shadow-card hover:bg-accent hover:text-ink hover:shadow-[1px_1px_0_rgba(0,0,0,1)] '
                : 'bg-stone-100 text-ink/20 cursor-not-allowed border-border/10'
            }`}
          >
            <Play size={14} />
            Genereer {selectedJobs.length} ruimte{selectedJobs.length !== 1 ? 's' : ''}
          </button>
        ) : (
          <div className="text-xs text-ink/50 uppercase tracking-wide">
            <Loader2 size={14} className="inline animate-spin mr-2" />
            {doneCount}/{selectedJobs.length} klaar{errorCount > 0 ? ` · ${errorCount} fout` : ''}
          </div>
        )}
        {!isRunning && (
          <p className="text-[10px] text-ink/50">
            {selectedJobs.length} credit{selectedJobs.length !== 1 ? 's' : ''} nodig
          </p>
        )}
      </div>
    </div>
  );
};
